import { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CalendarVersion, useCalendar, useCalendarRoster, useSaveCalendar, WorkCalendar, WorkInterval } from './calendar-queries';
import { performanceError } from './queries';

export const defaultCalendar = (): WorkCalendar => ({ mode: 'custom', zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  week: [[], ...Array.from({ length: 5 }, () => [{ start: 600, end: 1080 }]), []], exceptions: [] });
const clock = (minute: number) => `${String(Math.floor((minute % 1440) / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
function parseClock(value: string) { const [hour, minute] = value.split(':').map(Number); return hour * 60 + minute; }
function IntervalEditor({ value, onChange }: {value: WorkInterval[]; onChange: (value: WorkInterval[]) => void}) {
  const { t } = useTranslation();
  return <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
    {value.map((interval, index) => <Stack direction="row" spacing={1} key={index}>
      {(['start', 'end'] as const).map(field => <TextField key={field} type="time"
        label={t(`workflowPerformance.calendar_${field}`)} InputLabelProps={{ shrink: true }} value={clock(interval[field])}
        onChange={event => onChange(value.map((row, i) => i === index ? { ...row, [field]: field === 'end' && parseClock(event.target.value) === 0 ? 1440 : parseClock(event.target.value) } : row))} />)}
      <Button onClick={() => onChange(value.filter((_row, i) => i !== index))}>{t('workflowPerformance.calendarRemove')}</Button>
    </Stack>)}
    {value.length === 0 && <Typography>{t('workflowPerformance.calendarOff')}</Typography>}
    <Button disabled={value.length >= 2 || (value.at(-1)?.end ?? 0) >= 1440} onClick={() => onChange([...value, {
      start: value.at(-1)?.end ?? 600, end: value.length ? Math.min(1440, value[value.length - 1].end + 60) : 1080,
    }])}>
      {t('workflowPerformance.calendarAddInterval')}
    </Button>
  </Stack>;
}
function CalendarForm({ agency, kind, target, current, onSaved }: {
  agency: number; kind: string; target: string; current: CalendarVersion | null; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<WorkCalendar>(() => current ?? defaultCalendar());
  const mutation = useSaveCalendar(agency, kind, target);
  return <Stack spacing={2}>
    <Alert severity="info">{t('workflowPerformance.calendarHint')}</Alert>
    <Typography>{current ? t('workflowPerformance.calendarSince', { at: new Date(current.validFrom).toLocaleString() }) :
      t('workflowPerformance.calendarNotSaved')}</Typography>
    {kind === 'advisor' && <TextField select label={t('workflowPerformance.calendarMode')} value={draft.mode}
      onChange={event => setDraft(event.target.value === 'inherit' ? { ...draft, mode: 'inherit', week: Array.from({ length: 7 }, () => []), exceptions: [] } :
        { ...defaultCalendar(), zone: draft.zone })}>
      <MenuItem value="custom">{t('workflowPerformance.calendarPersonal')}</MenuItem>
      <MenuItem value="inherit">{t('workflowPerformance.calendarInherit')}</MenuItem>
    </TextField>}
    {draft.mode === 'custom' && <>
      <TextField label={t('workflowPerformance.calendarZone')} value={draft.zone}
        onChange={event => setDraft({ ...draft, zone: event.target.value })} helperText="America/Montevideo" />
      {draft.week.map((day, index) => <Stack key={index} spacing={1}>
        <Typography>{t(`workflowPerformance.calendarDay${index}`)}</Typography>
        <IntervalEditor value={day} onChange={value => setDraft({ ...draft,
          week: draft.week.map((row, i) => i === index ? value : row) })} />
      </Stack>)}
      <Typography variant="h6">{t('workflowPerformance.calendarExceptions')}</Typography>
      {draft.exceptions.map((exception, index) => <Stack key={index} spacing={1}>
        <TextField type="date" label={t('workflowPerformance.calendarDate')} InputLabelProps={{ shrink: true }} value={exception.date}
          onChange={event => setDraft({ ...draft,
            exceptions: draft.exceptions.map((row, i) => i === index ? { ...row, date: event.target.value } : row) })} />
        <IntervalEditor value={exception.intervals} onChange={intervals => setDraft({ ...draft,
          exceptions: draft.exceptions.map((row, i) => i === index ? { ...row, intervals } : row) })} />
        <Button onClick={() => setDraft({ ...draft, exceptions: draft.exceptions.filter((_row, i) => i !== index) })}>
          {t('workflowPerformance.calendarRemoveDate')}
        </Button>
      </Stack>)}
      <Button disabled={draft.exceptions.length >= 100} onClick={() => setDraft({ ...draft,
        exceptions: [...draft.exceptions, { date: '', intervals: [] }] })}>{t('workflowPerformance.calendarAddDate')}</Button>
    </>}
    {mutation.isError && <Alert severity="error">{t(performanceError(mutation.error))}</Alert>}
    <Button variant="contained" disabled={mutation.isLoading} onClick={() => mutation.mutate({
      revision: current?.id ?? null, mode: draft.mode, zone: draft.zone, week: draft.week, exceptions: draft.exceptions,
    }, { onSuccess: onSaved })}>{t('workflowPerformance.calendarSave')}</Button>
  </Stack>;
}
function CalendarSettings({ userId, agency }: {userId: string; agency: number}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState('advisor'), [target, setTarget] = useState('');
  const roster = useCalendarRoster(userId, agency);
  const query = useCalendar(userId, agency, kind, target);
  const choices = kind === 'advisor' ? roster.data?.advisors : roster.data?.teams;
  return <Stack spacing={2}>
    {(roster.isError || query.isError) && <Alert severity="error">{t(performanceError(roster.error ?? query.error))}</Alert>}
    {roster.data?.truncated && <Alert severity="warning">{t('workflowPerformance.groupsTruncated')}</Alert>}
    <Stack direction="row" spacing={1}>
      {(['advisor', 'team'] as const).map(value => <Button key={value} variant={kind === value ? 'contained' : 'outlined'}
        onClick={() => { setKind(value); setTarget(''); }}>{t(value === 'advisor' ? 'workflowPerformance.workloadAdvisor' : 'workflowPerformance.workloadTeam')}</Button>)}
    </Stack>
    <TextField select label={t('workflowPerformance.calendarTarget')} value={target} disabled={roster.isFetching || roster.isError}
      onChange={event => setTarget(event.target.value)}>
      <MenuItem value="">{t('workflowPerformance.choose')}</MenuItem>
      {choices?.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
    </TextField>
    <Button disabled={roster.isFetching || query.isFetching} onClick={() => {
      if (roster.isError || !target) void roster.refetch(); else void query.refetch();
    }}>{t('workflowPerformance.refresh')}</Button>
    {target && query.data && !query.isError && !roster.isError && !query.isFetching &&
      <CalendarForm key={`${kind}:${target}:${query.data.calendar?.id ?? 'new'}`} agency={agency} kind={kind} target={target}
        current={query.data.calendar} onSaved={() => void query.refetch()} />}
  </Stack>;
}
export function CalendarPanel({ userId, agency }: { userId: string; agency: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} aria-expanded={open} onClick={() => setOpen(!open)}>
        {t('workflowPerformance.calendars')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('workflowPerformance.calendars')}</DialogTitle>
        <DialogContent>{open && <CalendarSettings userId={userId} agency={agency} />}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('workflowPerformance.closeSettings')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
